import { config } from "../config";

export interface SocketMessage<T = unknown> {
  type: string;
  channel: string;
  ts: number;
  seq: number;
  payload: T;
}

type Listener = (msg: SocketMessage) => void;

interface Channel {
  ws: WebSocket | null;
  listeners: Set<Listener>;
  reconnectAttempts: number;
  closed: boolean;
  ticket?: string;
}

const channels = new Map<string, Channel>();

export async function subscribe(
  channel: string,
  ticketProvider: (channels: string[]) => Promise<string>,
  listener: Listener,
): Promise<() => void> {
  let ch = channels.get(channel);
  if (!ch) {
    ch = { ws: null, listeners: new Set(), reconnectAttempts: 0, closed: false };
    channels.set(channel, ch);
  }
  ch.listeners.add(listener);

  if (!ch.ws) {
    await connect(channel, ticketProvider);
  }

  return () => {
    const c = channels.get(channel);
    if (!c) return;
    c.listeners.delete(listener);
    if (c.listeners.size === 0) {
      c.closed = true;
      c.ws?.close();
      channels.delete(channel);
    }
  };
}

async function connect(
  channel: string,
  ticketProvider: (channels: string[]) => Promise<string>,
): Promise<void> {
  const ch = channels.get(channel);
  if (!ch || ch.closed) return;
  try {
    const ticket = await ticketProvider([channel]);
    ch.ticket = ticket;
    const url = `${config.wsBaseUrl}?channel=${encodeURIComponent(channel)}&ticket=${encodeURIComponent(ticket)}`;
    const ws = new WebSocket(url);
    ch.ws = ws;
    ws.addEventListener("open", () => {
      ch.reconnectAttempts = 0;
    });
    ws.addEventListener("message", (e) => {
      try {
        const msg = JSON.parse(e.data) as SocketMessage;
        for (const l of ch.listeners) l(msg);
      } catch {
        // ignore
      }
    });
    ws.addEventListener("close", () => {
      ch.ws = null;
      if (ch.closed || ch.listeners.size === 0) return;
      const delay = Math.min(30_000, 500 * 2 ** ch.reconnectAttempts);
      ch.reconnectAttempts += 1;
      setTimeout(() => connect(channel, ticketProvider), delay);
    });
    ws.addEventListener("error", () => {
      try {
        ws.close();
      } catch {
        // ignore
      }
    });
  } catch {
    const delay = Math.min(30_000, 500 * 2 ** ch.reconnectAttempts);
    ch.reconnectAttempts += 1;
    setTimeout(() => connect(channel, ticketProvider), delay);
  }
}
